// src/components/CookieConsent.tsx
// GDPR-compliant cookie consent banner

import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonItem,
  IonLabel,
  IonList,
  IonText,
} from '@ionic/react';
import { cookieConsentService, CookiePreferences } from '../services/cookieConsentService';
import './CookieConsent.css';

export const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    performance: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = cookieConsentService.hasConsent();
    setShowBanner(!hasConsent);
  }, []);

  const handleAcceptAll = () => {
    cookieConsentService.acceptAll();
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    cookieConsentService.acceptNecessaryOnly();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    cookieConsentService.saveConsent(preferences);
    setShowBanner(false);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-consent-overlay">
      <IonCard className="cookie-consent-card">
        <IonCardHeader>
          <IonCardTitle className="cookie-consent-title">
            🍪 Cookie Preferences
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {!showDetails ? (
            // Simple banner view
            <>
              <IonText className="cookie-consent-text">
                <p>
                  We use cookies to enhance your experience, analyze site usage, and assist in our
                  marketing efforts. By clicking "Accept All", you consent to our use of cookies.
                </p>
                <p style={{ fontSize: '14px', marginTop: '8px' }}>
                  <a
                    href="/docs/privacy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--ion-color-primary)' }}
                  >
                    Privacy Policy
                  </a>
                </p>
              </IonText>

              <div className="cookie-consent-actions">
                <IonButton
                  expand="block"
                  color="primary"
                  onClick={handleAcceptAll}
                >
                  Accept All
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={handleAcceptNecessary}
                >
                  Necessary Only
                </IonButton>
                <IonButton
                  expand="block"
                  fill="clear"
                  onClick={() => setShowDetails(true)}
                >
                  Customize
                </IonButton>
              </div>
            </>
          ) : (
            // Detailed preferences view
            <>
              <IonText className="cookie-consent-text">
                <p style={{ fontSize: '14px' }}>
                  Manage your cookie preferences. Necessary cookies are required for the site to
                  function and cannot be disabled.
                </p>
              </IonText>

              <IonList className="cookie-preferences-list">
                {/* Necessary cookies - always on */}
                <IonItem lines="full">
                  <IonCheckbox
                    slot="start"
                    checked={true}
                    disabled={true}
                    aria-label="Necessary cookies (required)"
                  />
                  <IonLabel>
                    <h3>Necessary</h3>
                    <p style={{ fontSize: '13px', color: 'var(--ion-color-medium)' }}>
                      Required for authentication, security, and basic site functionality.
                      Cannot be disabled.
                    </p>
                  </IonLabel>
                </IonItem>

                {/* Analytics cookies */}
                <IonItem lines="full">
                  <IonCheckbox
                    slot="start"
                    checked={preferences.analytics}
                    onIonChange={(e) => handlePreferenceChange('analytics', e.detail.checked)}
                    aria-label="Analytics cookies"
                  />
                  <IonLabel>
                    <h3>Analytics</h3>
                    <p style={{ fontSize: '13px', color: 'var(--ion-color-medium)' }}>
                      Help us understand how you use the site to improve your experience
                      (Sentry error tracking).
                    </p>
                  </IonLabel>
                </IonItem>

                {/* Performance cookies */}
                <IonItem lines="full">
                  <IonCheckbox
                    slot="start"
                    checked={preferences.performance}
                    onIonChange={(e) => handlePreferenceChange('performance', e.detail.checked)}
                    aria-label="Performance cookies"
                  />
                  <IonLabel>
                    <h3>Performance</h3>
                    <p style={{ fontSize: '13px', color: 'var(--ion-color-medium)' }}>
                      Monitor site performance and loading times to optimize speed.
                    </p>
                  </IonLabel>
                </IonItem>

                {/* Functional cookies */}
                <IonItem lines="none">
                  <IonCheckbox
                    slot="start"
                    checked={preferences.functional}
                    onIonChange={(e) => handlePreferenceChange('functional', e.detail.checked)}
                    aria-label="Functional cookies"
                  />
                  <IonLabel>
                    <h3>Functional</h3>
                    <p style={{ fontSize: '13px', color: 'var(--ion-color-medium)' }}>
                      Remember your preferences and settings (theme, language, etc.).
                    </p>
                  </IonLabel>
                </IonItem>
              </IonList>

              <div className="cookie-consent-actions">
                <IonButton
                  expand="block"
                  color="primary"
                  onClick={handleSavePreferences}
                >
                  Save Preferences
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => setShowDetails(false)}
                >
                  Back
                </IonButton>
              </div>
            </>
          )}
        </IonCardContent>
      </IonCard>
    </div>
  );
};
